import type { AgentPublicEvent } from "@wags/agent-runtime/event.contract.ts";

export interface AgentSmoothStreamOptions {
    delayMs?: number;
    chunking?: "word" | "line";
}

export const DEFAULT_SMOOTH_STREAM_DELAY_MS = 15;

type MessageDeltaPayload = Extract<
    AgentPublicEvent["payload"],
    { type: "message.delta" }
>;

function chunkText(text: string, chunking: "word" | "line"): string[] {
    const pattern = chunking === "line" ? /[^\n]*\n|[^\n]+$/gu : /\s*\S+\s*/gu;
    return text.match(pattern) ?? [text];
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        if (signal?.aborted === true) {
            resolve();
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        signal?.addEventListener("abort", onAbort, { once: true });
    });
}

interface QueueItem {
    event?: AgentPublicEvent;
    error?: unknown;
    done?: true;
}

interface PendingSource {
    event: AgentPublicEvent;
    payload: MessageDeltaPayload;
    remaining: number;
}

interface SmoothBuffer {
    pendingSources: PendingSource[];
    lastMessagePayload: MessageDeltaPayload | undefined;
    pendingText: string;
    holdback: AgentPublicEvent | undefined;
    ended: QueueItem | undefined;
}

function takeBufferedChunk(
    buffer: SmoothBuffer,
    chunking: "word" | "line",
    boundary: boolean,
): { event: AgentPublicEvent; delay: boolean } | "continue" | null {
    if (buffer.pendingText.length === 0 || buffer.pendingSources.length === 0) {
        return null;
    }
    const chunks = chunkText(buffer.pendingText, chunking);
    const chunk = chunks[0];
    if (chunk === undefined || (chunks.length <= 1 && !boundary)) {
        return null;
    }
    buffer.pendingText = buffer.pendingText.slice(chunk.length);
    let drained: PendingSource | undefined;
    let consumed = chunk.length;
    while (
        consumed > 0 &&
        buffer.pendingSources[0] !== undefined &&
        buffer.pendingSources[0].remaining <= consumed
    ) {
        drained = buffer.pendingSources[0];
        consumed -= drained.remaining;
        buffer.pendingSources.shift();
    }
    const partial = buffer.pendingSources[0];
    if (consumed > 0 && partial !== undefined) {
        partial.remaining -= consumed;
    }
    const source = drained ?? partial;
    if (source === undefined) {
        return "continue";
    }
    return {
        event: {
            ...source.event,
            cursor:
                drained !== undefined
                    ? drained.event.cursor
                    : source.event.cursor - 1,
            payload: { ...source.payload, text: chunk },
        },
        delay: buffer.pendingText.length > 0 || !boundary,
    };
}

function flushHoldback(
    buffer: SmoothBuffer,
): { yieldEvent?: AgentPublicEvent } | null {
    if (buffer.pendingText.length > 0 || buffer.holdback === undefined) {
        return null;
    }
    const event = buffer.holdback;
    buffer.holdback = undefined;
    if (event.payload.type === "message.delta") {
        buffer.pendingSources.push({
            event,
            payload: event.payload,
            remaining: event.payload.text.length,
        });
        buffer.lastMessagePayload = event.payload;
        buffer.pendingText = event.payload.text;
        return {};
    }
    return { yieldEvent: event };
}

function ingestQueueItem(buffer: SmoothBuffer, item: QueueItem): void {
    if (item.event === undefined) {
        buffer.ended = item;
        return;
    }
    const event = item.event;
    if (
        event.payload.type === "message.delta" &&
        (buffer.lastMessagePayload === undefined ||
            buffer.pendingText.length === 0 ||
            buffer.lastMessagePayload.messageId === event.payload.messageId)
    ) {
        if (buffer.pendingText.length === 0) {
            buffer.pendingSources.length = 0;
        }
        buffer.pendingSources.push({
            event,
            payload: event.payload,
            remaining: event.payload.text.length,
        });
        buffer.lastMessagePayload = event.payload;
        buffer.pendingText += event.payload.text;
        return;
    }
    buffer.holdback = event;
}

function startEventPump(
    iterator: AsyncIterator<AgentPublicEvent>,
    queue: QueueItem[],
    notify: () => void,
): void {
    void (async () => {
        try {
            while (true) {
                const next = await iterator.next();
                if (next.done === true) {
                    queue.push({ done: true });
                    break;
                }
                queue.push({ event: next.value });
                notify();
            }
        } catch (error) {
            queue.push({ error });
        } finally {
            notify();
        }
    })();
}

export async function* smoothAgentStream(
    events: AsyncIterable<AgentPublicEvent>,
    options: AgentSmoothStreamOptions = {},
    signal?: AbortSignal,
): AsyncIterable<AgentPublicEvent> {
    const delayMs = options.delayMs ?? DEFAULT_SMOOTH_STREAM_DELAY_MS;
    const chunking = options.chunking ?? "word";
    if (delayMs <= 0) {
        yield* events;
        return;
    }

    const queue: QueueItem[] = [];
    let wake = () => {};
    const notify = () => {
        wake();
        wake = () => {};
    };
    const iterator = events[Symbol.asyncIterator]();
    startEventPump(iterator, queue, notify);

    const buffer: SmoothBuffer = {
        pendingSources: [],
        lastMessagePayload: undefined,
        pendingText: "",
        holdback: undefined,
        ended: undefined,
    };

    try {
        while (true) {
            const boundary =
                buffer.holdback !== undefined || buffer.ended !== undefined;
            const buffered = takeBufferedChunk(buffer, chunking, boundary);
            if (buffered === "continue") {
                continue;
            }
            if (buffered !== null) {
                yield buffered.event;
                if (buffered.delay) {
                    await sleep(delayMs, signal);
                }
                continue;
            }
            const flushed = flushHoldback(buffer);
            if (flushed !== null) {
                if (flushed.yieldEvent !== undefined) {
                    yield flushed.yieldEvent;
                }
                continue;
            }
            const item = queue.shift();
            if (item !== undefined) {
                ingestQueueItem(buffer, item);
                continue;
            }
            if (buffer.ended !== undefined) {
                if (buffer.ended.error !== undefined) throw buffer.ended.error;
                return;
            }
            await new Promise<void>((resolve) => {
                wake = resolve;
                if (queue.length > 0) resolve();
            });
        }
    } finally {
        void iterator.return?.();
    }
}
